import ReactECharts from 'echarts-for-react'
import * as echarts from 'echarts'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useApp } from '../../context/AppContext'
import { countryName } from '../../lib/activityMap'
import type { MarketScore } from '../../lib/types'
import { Card, CardContent, CardHeader, CardTitle, Badge } from '../ui'
import { Plane, Navigation, Sparkles } from 'lucide-react'
import { ChartProvenanceCallout } from '../ChartProvenanceCallout'
import europeGeoJson from './europe.json'

// Registrar el mapa de Europa en ECharts
if (!echarts.getMap('europe')) {
  echarts.registerMap('europe', europeGeoJson as any)
}

// Coordenadas geo optimizadas para separar visualmente las etiquetas y hubs
const AIRPORT_COORDS: Record<string, [number, number]> = {
  KTT: [24.8468, 67.7010], // Kittilä (Levi Gateway)
  GB: [-1.5000, 52.6000],  // UK (Centrado en Gran Bretaña)
  DE: [10.2000, 51.1000],  // Germany (Centro de Alemania)
  FR: [2.2000, 46.8000],   // France (Centro de Francia)
  NL: [5.2913, 52.1326],   // Netherlands
  ES: [-3.7038, 40.4168],  // Spain (Madrid)
  IT: [12.5674, 42.5000],  // Italy (Roma)
  SE: [16.5000, 61.5000],  // Sweden
  NO: [9.5000, 61.0000],   // Norway
  FI: [25.5000, 61.2000],  // Southern Finland
}

// Configuración de posición de la etiqueta para evitar solapamientos
const LABEL_POSITION: Record<string, 'left' | 'right' | 'top' | 'bottom'> = {
  GB: 'left',
  NL: 'top',
  DE: 'right',
  FR: 'bottom',
  ES: 'bottom',
  IT: 'right',
  SE: 'right',
  NO: 'left',
  FI: 'bottom',
}



interface EuropeFlightMapProps {
  markets: MarketScore[]
  leadingMarket?: string | null
}

export function EuropeFlightMap({ markets, leadingMarket }: EuropeFlightMapProps) {
  const { t } = useTranslation()
  const { theme, locale } = useApp()

  const destCoord = AIRPORT_COORDS['KTT']

  const chartOptions = useMemo(() => {
    const isLapland = theme === 'lapland'
    const isDark = theme !== 'light'

    const bgColor = isLapland ? '#0d1326' : isDark ? '#0b1120' : '#0f172a'
    const mapAreaColor = isLapland ? '#16203c' : '#1e293b'
    const mapBorderColor = isLapland ? '#2dd4bf33' : '#38bdf833'
    const primaryGlow = isLapland ? '#2dd4bf' : '#38bdf8'
    const highlightGlow = '#f59e0b'
    const textColor = '#e2e8f0'

    const flightLinesData: any[] = []
    const scatterHubs: any[] = []

    // Hub destino: Kittilä
    scatterHubs.push({
      name: 'Kittilä Airport (Levi Gateway)',
      value: [destCoord[0], destCoord[1], 100],
      isDest: true,
      country: 'FI',
      share: 1,
    })

    // Top-5 mercados EMISORES: se excluye Finlandia (es el destino, su linea
    // seria un vuelo domestico trivial) y se toman los 5 mejores extranjeros.
    const sourceMarkets = markets.filter((m) => m.country !== 'FI').slice(0, 5)

    sourceMarkets.forEach((m) => {
      const origin = AIRPORT_COORDS[m.country]
      if (origin) {
        const isLeading = m.country === leadingMarket
        const shareVal = m.share ?? m.score * 0.4
        const localizedName = countryName(m.country, locale) || m.country

        scatterHubs.push({
          name: localizedName,
          value: [origin[0], origin[1], Math.round(shareVal * 100)],
          isDest: false,
          isLeading,
          country: m.country,
          share: shareVal,
          score: m.score,
        })

        flightLinesData.push({
          fromName: localizedName,
          toName: 'Kittilä (Levi)',
          coords: [origin, destCoord],
          isLeading,
          share: shareVal,
          lineStyle: {
            color: isLeading ? highlightGlow : primaryGlow,
            width: isLeading ? 2.8 : 1.8,
            opacity: 0.7,
            curveness: 0.28,
          },
        })
      }
    })

    return {
      backgroundColor: bgColor,
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        borderColor: 'rgba(56, 189, 248, 0.3)',
        borderWidth: 1,
        textStyle: { color: textColor, fontSize: 12 },
        formatter: (params: any) => {
          if (params.seriesType === 'lines') {
            const d = params.data
            return (
              `<div class="p-1 space-y-1">` +
              `<div class="font-bold text-sm text-sky-400">${t('map.flightTooltipTitle', '✈ Corredor de Vuelo a Laponia')}</div>` +
              `<div class="text-xs text-slate-300">${d.fromName} → <strong>Kittilä (KTT)</strong></div>` +
              `<div class="text-xs text-amber-400 font-mono mt-1">${t('map.flightDemandShare', 'Cuota de demanda')}: ${Math.round(d.share * 100)}%</div>` +
              `</div>`
            )
          }
          if (params.seriesType === 'effectScatter') {
            const d = params.data
            if (d.isDest) {
              return (
                `<div class="p-1">` +
                `<div class="font-bold text-sm text-emerald-400">${t('map.flightDestTitle', '★ Destino Kittilä / Levi (KTT)')}</div>` +
                `<div class="text-xs text-slate-300">${t('map.flightDestDesc', 'Punto de llegada de los viajeros árticos')}</div>` +
                `</div>`
              )
            }
            return (
              `<div class="p-1">` +
              `<div class="font-bold text-sm text-white">${d.name}</div>` +
              `<div class="text-xs text-slate-300">${t('map.flightMarketShare', 'Cuota de Mercado')}: <strong>${Math.round(d.share * 100)}%</strong></div>` +
              `<div class="text-xs text-sky-400 font-mono">${t('map.flightModelScore', 'Score del modelo')}: ${d.score?.toFixed(2)}</div>` +
              `${d.isLeading ? `<div class="text-[11px] text-amber-400 font-semibold mt-1">${t('map.flightPriority1', '★ Mercado prioritario #1')}</div>` : ''}` +
              `</div>`
            )
          }
          return null
        },
      },
      geo: {
        map: 'europe',
        roam: true,
        zoom: 1.85, // Zoom equilibrado para encajar todo el corredor sin recortar Laponia
        center: [14, 60], // Centrado ligeramente más al norte (lat 60) para dar margen superior a Levi / Kittilä
        label: {
          show: false,
        },
        itemStyle: {
          areaColor: mapAreaColor,
          borderColor: mapBorderColor,
          borderWidth: 1.2,
        },
        emphasis: {
          itemStyle: {
            areaColor: isLapland ? '#1e2e54' : '#24344d',
            borderColor: primaryGlow,
          },
          label: { show: false },
        },
      },
      series: [
        // Flujos aéreos con estelas de luz
        {
          name: 'Flujos aéreos',
          type: 'lines',
          coordinateSystem: 'geo',
          zlevel: 2,
          effect: {
            show: true,
            period: 3.5,
            trailLength: 0.65,
            symbol: 'arrow',
            symbolSize: 6,
            color: (params: any) => (params.data?.isLeading ? highlightGlow : '#38bdf8'),
          },
          lineStyle: {
            curveness: 0.28,
          },
          data: flightLinesData,
        },
        // Líneas fijas suaves
        {
          type: 'lines',
          coordinateSystem: 'geo',
          zlevel: 1,
          lineStyle: {
            curveness: 0.28,
          },
          data: flightLinesData,
        },
        // Destino Levi / Kittilä
        {
          type: 'effectScatter',
          coordinateSystem: 'geo',
          zlevel: 3,
          rippleEffect: {
            brushType: 'stroke',
            scale: 5,
            period: 3,
          },
          label: {
            show: true,
            position: 'left', // Al situarlo a la izquierda del punto no colisiona con el borde superior ni con la leyenda
            distance: 10,
            formatter: '★ Kittilä / Levi (KTT)',
            fontSize: 11,
            fontWeight: 'bold',
            color: '#34d399',
            backgroundColor: 'rgba(15, 23, 42, 0.9)',
            padding: [4, 8],
            borderRadius: 4,
            borderColor: '#10b981',
            borderWidth: 1,
          },
          itemStyle: {
            color: '#10b981',
            shadowBlur: 15,
            shadowColor: '#10b981',
          },
          data: [scatterHubs[0]],
        },
        // Hubs emisores con posición de etiquetas inteligente
        {
          type: 'effectScatter',
          coordinateSystem: 'geo',
          zlevel: 3,
          rippleEffect: {
            brushType: 'stroke',
            scale: 3.5,
            period: 4,
          },
          symbolSize: (val: any) => Math.max(12, Math.min(24, val[2] * 0.35 + 8)),
          label: {
            show: true,
            position: (p: any) => LABEL_POSITION[p.data.country] || 'bottom',
            distance: 8,
            formatter: (p: any) => `${p.data.name}\n${p.data.value[2]}%`,
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 14,
            align: 'center',
            color: textColor,
            backgroundColor: 'rgba(15, 23, 42, 0.85)',
            padding: [3, 6],
            borderRadius: 4,
            borderColor: 'rgba(56, 189, 248, 0.2)',
            borderWidth: 1,
          },
          itemStyle: {
            color: (p: any) => (p.data.isLeading ? highlightGlow : primaryGlow),
            shadowBlur: 10,
            shadowColor: primaryGlow,
          },
          data: scatterHubs.slice(1),
        },
      ],
    }
  }, [markets, leadingMarket, theme, locale, destCoord, t])

  return (
    <Card className="isolate relative z-0 overflow-hidden border border-sky-500/20 shadow-xl bg-card">
      <CardHeader className="pb-3 bg-gradient-to-r from-card via-card/90 to-background border-b">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div className="flex size-8 items-center justify-center rounded-lg bg-sky-500/10 text-sky-400">
              <Plane className="size-4 animate-pulse" />
            </div>
            <div>
              <CardTitle className="text-base font-bold text-foreground">
                {t('map.corridorTitle', 'Corredores de Tráfico Aéreo hacia Kittilä / Levi (KTT)')}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t('map.corridorSubtitle', 'Top-5 mercados emisores hacia Kittilä (KTT) según señal ponderada de demanda (cuota real, búsquedas de vuelo, tráfico web y encaje de perfil). Se excluye Finlandia, que es el destino.')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[11px] gap-1.5 border-cyan-500/40 text-cyan-400 bg-cyan-500/10">
              <Sparkles className="size-3 text-cyan-400" />
              {t('map.top5', 'Top-5 mercados emisores')}
            </Badge>
            <Badge variant="outline" className="text-[11px] gap-1.5 border-amber-500/40 text-amber-400 bg-amber-500/10">
              <Sparkles className="size-3 text-amber-400" />
              {leadingMarket ? `${t('map.topMarket', 'Kärkimarkkina')}: ${leadingMarket}` : t('map.gateway', 'Kittilän portti')}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0 sm:p-2 bg-slate-950">
        {/* Altura vertical ampliada de 400px a 560px para un gran espacio visual */}
        <div className="h-[560px] w-full rounded-b-xl sm:rounded-lg overflow-hidden relative">
          <ReactECharts
            option={chartOptions}
            style={{ height: '100%', width: '100%' }}
            notMerge
            lazyUpdate
          />

          {/* Leyenda flotante */}
          <div className="absolute top-4 right-4 z-10 pointer-events-none rounded-lg bg-slate-900/90 p-3 text-xs text-slate-300 backdrop-blur-md border border-slate-800 shadow-xl space-y-2">
            <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">
              {t('map.flightLegendTitle', 'Leyenda de Flujo Aéreo')}
            </div>
            <div className="flex items-center gap-2">
              <span className="size-2.5 rounded-full bg-amber-400 shadow-sm shadow-amber-400/50" />
              <span className="text-[11px]">{t('map.legendPriority', 'Mercado Prioritario #1 (Mayor Inversión)')}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="size-2.5 rounded-full bg-sky-400 shadow-sm shadow-sky-400/50" />
              <span className="text-[11px]">{t('map.legendComplementary', 'Mercados Complementarios de Europa')}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="size-2.5 rounded-full bg-emerald-400 animate-ping" />
              <span className="text-[11px] font-semibold text-emerald-300">{t('map.legendDestKtt', 'Destino Kittilä Airport (Levi)')}</span>
            </div>
          </div>
        </div>

        <div className="p-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-800 text-xs text-slate-400 bg-slate-900/50">
          <p className="flex items-center gap-1.5">
            <Navigation className="size-3.5 text-sky-400" />
            <span>{t('map.flightParticlesDesc', 'Las partículas luminosas representan el volumen relativo de búsquedas y reservas aéreas hacia Kittilä.')}</span>
          </p>
          <span className="font-mono text-[10px] text-slate-500">{t('map.interactiveZoom', 'Zoom interactivo habilitado · ECharts Vector Flow')}</span>
        </div>

        <div className="p-3 border-t border-slate-800 bg-slate-950">
          <ChartProvenanceCallout
            sourceName={t('chartExpl.flightMap.sourceName')}
            sourceType="mixed"
            sourceDesc={t('chartExpl.flightMap.sourceDesc')}
            insight={t('chartExpl.flightMap.insight')}
          />
        </div>
      </CardContent>
    </Card>
  )
}
